import { ADMIN_SECTIONS, NameOfChildRoutes, NamesOfParentRoutes, PERMISSION, ROLE } from "@shared/constants";

import { User } from "../models";
import { tokenHandler } from "../utils";

export const getUserPermissions = (userParam?: User): string[] => {
  const user: User = userParam || tokenHandler.getUser();

  if (!user || !user.roles) {
    return [];
  }

  const permissions: string[] = [];
  user.roles.forEach((r) => {
    permissions.push(...(r.permissions ? r.permissions.map((p) => p.name) : []));
  });

  return permissions;
};

export const checkPermission = (permission: string, user?: User): boolean => {
  const userPermissions = getUserPermissions(user);
  return !!userPermissions.find((up) => up === permission);
};

export const isUserContractor = (userForCheck?: User): boolean => {
  const user: User = userForCheck || tokenHandler.getUser();
  if (user) {
    return Boolean(user.roles?.find((r) => r.name === ROLE.CONTRACTOR)) || checkPermission(PERMISSION.CONTRACTOR_MODE);
  } else {
    return false;
  }
};

// Mirrors isUserContractor's role-name .find shape, minus its `|| checkPermission(...MODE)`
// fallback clause — there is no HR "mode" permission to fall back to. Deliberately checks the
// ROLE, not a permission (unlike isUserTeamLead below, which ignores its argument and checks
// PERMISSION.TEAM_LEAD_MODE instead — not the shape to imitate here).
export const isUserHrOrSuperAdmin = (userForCheck?: User): boolean => {
  const user: User = userForCheck || tokenHandler.getUser();
  if (user) {
    return Boolean(user.roles?.find((r) => r.name === ROLE.HR || r.name === ROLE.SUPER_ADMIN));
  } else {
    return false;
  }
};

export const isUserTeamLead = (userForCheck?: User): boolean => {
  const user: User = userForCheck || tokenHandler.getUser();
  if (user) {
    return checkPermission(PERMISSION.TEAM_LEAD_MODE);
  } else {
    return false;
  }
};

export const checkPermissions = (permissions: string[], user?: User): boolean => {
  const userPermissions = getUserPermissions(user);
  return permissions.some((p) => userPermissions.find((up) => up === p));
};

const getUserUrlBeforeLogout = (): string | null => {
  const redirectUrl = localStorage.getItem("redirectUrl");

  localStorage.removeItem("redirectUrl");

  if (redirectUrl) {
    return redirectUrl;
  }

  return null;
};

export const getDefaultUrl = (user: User, onError: () => void) => {
  if (
    checkPermission(PERMISSION.VIEW_PROCEDURES, user) &&
    (isUserContractor(user) || checkPermission(PERMISSION.CONTRACTOR_MODE))
  ) {
    return getUserUrlBeforeLogout() || `${NameOfChildRoutes.PROCEDURES.ROOT}${NameOfChildRoutes.PROCEDURES.PROCESSES}`;
  }
  if (checkPermission(PERMISSION.VIEW_USERS, user)) {
    return getUserUrlBeforeLogout() || NameOfChildRoutes.USERS.ROOT;
  }
  // A user who holds none of the above but DOES hold at least one registered admin section's
  // permission (e.g. Phase 21's VIEW_PROCEDURE_INDEX_STATUS) — someone who is not a Super Admin and
  // has neither VIEW_PROCEDURES nor VIEW_USERS, which the Admin section's whole design exists to
  // support. Without this branch such a user falls to onError() below, which wipes their entire
  // session (tokenHandler.removeAll()) on every login — a legitimately authenticated user locked
  // out of the one area they actually have access to.
  if (
    checkPermissions(
      ADMIN_SECTIONS.map((section) => section.permission),
      user,
    )
  ) {
    return getUserUrlBeforeLogout() || NameOfChildRoutes.ADMIN.ROOT;
  }

  onError();

  return `${NamesOfParentRoutes.AUTH}${NameOfChildRoutes.AUTH.LOGIN}`;
};
